/**
 * Vitolier platform sharing — client for share events, attribution tokens and
 * external share intents.
 *
 * Privacy rules enforced here:
 *  - a share is only recorded as completed when the platform can verify it
 *    (native share promise resolved, or link copied);
 *  - external social selections are recorded as initiated, never completed;
 *  - recipients, contact lists and share-sheet contents are never captured;
 *  - no third-party tracking scripts or social SDKs are loaded.
 */
import { supabase } from "@/integrations/supabase/client";
import type { ShareEntityType } from "@/lib/sharing/registry";

export type ShareChannel =
  | "native_share"
  | "copy_link"
  | "whatsapp"
  | "email"
  | "x"
  | "facebook"
  | "linkedin"
  | "other";

export type ShareEventType = "share_completed" | "share_initiated" | "link_copied";

export type ShareSurface =
  | "card"
  | "detail_page"
  | "action_bar"
  | "dashboard"
  | "history"
  | "overflow_menu";

export interface RecordShareInput {
  entityType: ShareEntityType;
  entityId: string;
  canonicalUrl: string;
  channel: ShareChannel;
  eventType: ShareEventType;
  surface?: ShareSurface;
  organisationId?: string | null;
  shareToken?: string | null;
}

/** Record a share event. Never throws — analytics must not break sharing. */
export async function recordShareEvent(input: RecordShareInput): Promise<void> {
  try {
    await (supabase as any).rpc("record_content_share", {
      _entity_type: input.entityType,
      _entity_id: input.entityId,
      _canonical_url: input.canonicalUrl,
      _channel: input.channel,
      _event_type: input.eventType,
      _source_surface: input.surface ?? null,
      _organisation_id: input.organisationId ?? null,
      _share_token: input.shareToken ?? null,
    });
  } catch {
    /* ignore — recording is best effort */
  }
}

/** Create (or reuse) the signed-in member's opaque attribution token. */
export async function createShareLinkToken(args: {
  entityType: ShareEntityType;
  entityId: string;
  organisationId?: string | null;
}): Promise<string | null> {
  try {
    const { data, error } = await (supabase as any).rpc("create_content_share_link", {
      _entity_type: args.entityType,
      _entity_id: args.entityId,
      _organisation_id: args.organisationId ?? null,
    });
    if (error) return null;
    return (data as string | null) ?? null;
  } catch {
    return null;
  }
}

/** Record a visit that arrived from a shared link. */
export async function recordShareVisit(token: string, conversionType?: string): Promise<void> {
  try {
    await (supabase as any).rpc("record_content_share_visit", {
      _token: token,
      _conversion_type: conversionType ?? null,
    });
  } catch {
    /* ignore */
  }
}

export const SHARE_TOKEN_PATTERN = /^VS[A-Z0-9]{6,20}$/;

export function normaliseShareToken(raw: string | null | undefined): string | null {
  const v = (raw ?? "").trim().toUpperCase();
  return SHARE_TOKEN_PATTERN.test(v) ? v : null;
}

/* ------------------------------------------------------------------ */
/*  External share intents (no SDKs, fully encoded)                     */
/* ------------------------------------------------------------------ */

export interface ShareIntentPayload {
  url: string;
  title: string;
  text?: string;
  /**
   * Crawler-readable preview page for the same content. Social platforms read
   * metadata from the link they are given, so networks receive this URL while
   * people are still forwarded to the canonical Vitolier page.
   */
  previewUrl?: string | null;
}

export function buildShareIntentUrl(channel: ShareChannel, payload: ShareIntentPayload): string | null {
  const linkForNetworks = payload.previewUrl || payload.url;
  const url = encodeURIComponent(linkForNetworks);
  const title = encodeURIComponent(payload.title);
  const message = encodeURIComponent(`${payload.title}${payload.text ? ` — ${payload.text}` : ""}`);
  const body = encodeURIComponent(
    `${payload.text ? `${payload.text}\n\n` : ""}${payload.url}`,
  );

  switch (channel) {
    case "whatsapp":
      return `https://wa.me/?text=${message}%20${url}`;
    case "email":
      // Email shows the canonical link, which is what the reader should keep.
      return `mailto:?subject=${title}&body=${body}`;
    case "x":
      return `https://twitter.com/intent/tweet?text=${message}&url=${url}`;
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    case "linkedin":
      return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    default:
      return null;
  }
}


export function supportsNativeShare(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

export async function copyToClipboard(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    const el = document.createElement("textarea");
    el.value = value;
    el.setAttribute("readonly", "true");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

export function shareChannelLabel(channel: string): string {
  switch (channel) {
    case "native_share":
      return "Device share sheet";
    case "copy_link":
      return "Copied link";
    case "whatsapp":
      return "WhatsApp";
    case "email":
      return "Email";
    case "x":
      return "X";
    case "facebook":
      return "Facebook";
    case "linkedin":
      return "LinkedIn";
    default:
      return "Other";
  }
}
